import { type ActionEvent, Controller } from "@hotwired/stimulus"

import DrawerController from "../drawer_controller"

import BookingFooterController from "./booking_footer_controller"
import RoomCounterController from "./room_counter_controller"

import { formatEEEDMMMYYYY } from "@/helpers/date_format"
import * as Ga4 from "@/helpers/ga4_helper"
import { loadBackgroundImages } from "@/helpers/image_loader"
import { pluralize } from "@/helpers/pluralize"
import { ENTRY, prefetchEntry } from "@/helpers/prefetch"
import { setInputValueWithEvents } from "@/helpers/utils"

export default class extends Controller {
  static targets = [
    "calendarDrawerSummary",
    "calendarEditButton",
    "calendarStream",
    "dateRadio",
    "facilitiesDrawerSummary",
    "facilitiesStream",
    "facilityRadio",
    "formSubmitButton",
    "hiddenDateInput",
    "hiddenPaxInput",
    "hiddenFacilityInput",
    "hiddenRoomsInputsContainer",
    "lastStepEditButton",
    "maxPaxNotice",
    "modalHeader",
    "paxInput",
    "paxSummary",
    "roomsDrawerSummary",
    "roomsStream",
    "unavailableDateNotice",
    "unavailablePackageNotice"
  ]
  declare readonly calendarDrawerSummaryTarget: HTMLElement
  declare readonly calendarEditButtonTarget: HTMLElement
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly facilitiesDrawerSummaryTarget: HTMLElement
  declare readonly facilitiesStreamTarget: HTMLElement
  declare readonly facilityRadioTargets: HTMLInputElement[]
  declare readonly formSubmitButtonTarget: HTMLButtonElement
  declare readonly hiddenDateInputTargets: HTMLInputElement[]
  declare readonly hiddenPaxInputTargets: HTMLInputElement[]
  declare readonly hiddenFacilityInputTargets: HTMLInputElement[]
  declare readonly hiddenRoomsInputsContainerTargets: HTMLDivElement[]
  declare readonly lastStepEditButtonTarget: HTMLElement
  declare readonly maxPaxNoticeTarget: HTMLElement
  declare readonly modalHeaderTarget: HTMLElement
  declare readonly paxInputTarget: HTMLInputElement
  declare readonly paxSummaryTarget: HTMLElement
  declare readonly roomsDrawerSummaryTarget: HTMLElement
  declare readonly roomsStreamTarget: HTMLElement
  declare readonly unavailableDateNoticeTarget: HTMLElement

  declare readonly hasCalendarStreamTarget: boolean
  declare readonly hasRoomsStreamTarget: boolean
  declare readonly hasUnavailablePackageNoticeTarget: boolean

  static outlets = ["booking-footer", "drawer", "room-counter"]
  declare readonly bookingFooterOutlet: BookingFooterController
  declare readonly drawerOutlets: DrawerController[]
  declare readonly roomCounterOutlets: RoomCounterController[]

  static values = { ecommerceItem: Object, nights: Number, price: Number }
  declare readonly ecommerceItemValue: Ga4.EcommerceItem
  declare readonly nightsValue: number
  declare readonly priceValue: number

  declare totalPrice: number

  calendarStreamTargetConnected(): void {
    const isDateSelected = !!this.selectedDateRadio

    prefetchEntry(ENTRY.BOOKINGS)

    // Hide notice when date is selected. We don't show it in the opposite case,
    // otherwise notice would show on load whenever there's no date in params, which
    // we don't want (if no date was selected, it can't be "unavailable").
    if (isDateSelected) {
      this.unavailableDateNoticeTarget.hidden = true
    }

    this.setCalendarSubmit(isDateSelected)
  }

  facilitiesStreamTargetConnected(): void {
    this.bookingFooterOutlet.setFacilitiesSubmit(!!this.selectedFacilityRadio)

    if (this.selectedFacilityRadio) {
      this.hiddenFacilityInputTargets.forEach(
        (input) => (input.value = this.selectedFacilityRadio!.value)
      )
    }
  }

  unavailablePackageNoticeTargetConnected(): void {
    this.bookingFooterOutlet.submitButtonTarget.disabled = true
  }

  roomsStreamTargetConnected(element: HTMLElement): void {
    // Load fallback image for rooms with no carousel
    loadBackgroundImages(element.querySelectorAll("[data-img]"))

    const remainingPax = this.selectedPax - this.paxForSelectedRooms

    this.roomCounterOutlets.forEach((counter) => {
      counter.setMax(remainingPax)
    })
    this.bookingFooterOutlet.setRoomsSubmit(remainingPax === 0)
  }

  bookingFooterOutletConnected(outlet: BookingFooterController): void {
    if (this.isSpaDay && this.isDateSelected && this.priceValue) {
      this.totalPrice = this.priceValue * this.selectedPax
      outlet.setPriceTarget(this.totalPrice)
      outlet.priceSummaryTarget.hidden = false
    }
  }

  addToCart(): void {
    Ga4.addToCartEvent(
      {
        ...this.ecommerceItemValue,
        price: this.totalPrice,
        quantity: 1
      },
      this.totalPrice,
      "booking"
    )
  }

  confirmPax({ params: { price } }: ActionEvent): void {
    this.paxSummaryTarget.textContent = pluralize(this.selectedPax, "guest")

    // Confirming pax goes to calendar step and price summary updated there, except:
    // 1. If page loaded with date param there's no calendar and thus no "selected" date. In that
    //    case we get the price from server as a param to update price summary.
    if (price) {
      this.bookingFooterOutlet.setPriceTarget(+price * this.selectedPax)
    }

    // 2. If date is already selected (e.g. going back to pax step from another step). In that
    //    case re-select saved date to trigger #dateSelected (which fires on `change`, not `click`)
    //    to update price summary for latest pax value.
    const { selectedDateRadio } = this

    if (selectedDateRadio) {
      setInputValueWithEvents(selectedDateRadio, selectedDateRadio.value, ["change"])
    }
  }

  dateSelected({ params: { price }, target }: ActionEvent): void {
    this.unavailableDateNoticeTarget.hidden = true

    if (price && (target as HTMLInputElement).checked) {
      this.totalPrice = +price * this.selectedPax
      this.bookingFooterOutlet.setPriceTarget(this.totalPrice)
      this.setCalendarSubmit(true)
      // Update the hidden date targets in the pax and facilities/rooms drawers, so it will overwrite any date from params.
      // These inputs are necessary to enable skipping the calendar drawer if date already present,
      // or submit the form from the date drawer when all downstream drawers have already been completed.
      this.hiddenDateInputTargets.forEach(
        (input) => (input.value = (target as HTMLInputElement).value)
      )
    } else {
      this.setCalendarSubmit(false)
      this.hiddenDateInputTargets.forEach((input) => (input.value = ""))
    }
  }

  facilitySelected(): void {
    this.bookingFooterOutlet.setFacilitiesSubmit(true)
    this.hiddenFacilityInputTargets.forEach(
      (input) => (input.value = this.selectedFacilityRadio!.value)
    )
  }

  onCalendarDrawerClick(e: ActionEvent): void {
    // Calendar drawer should fetch fresh calendar in two cases:
    // 1. Arriving at calendar from pax step (i.e. pax drawer is open)
    // 2. Calendar has never been loaded yet (e.g. date already in URL params)
    if (
      this.isPaxDrawerOpen ||
      !this.hasCalendarStreamTarget ||
      this.hasUnavailablePackageNoticeTarget
    ) {
      e.stopPropagation()
      this.calendarEditButtonTarget.click()
    } else {
      this.calendarStreamTargetConnected()
    }
  }

  onRoomOrFacilityDrawerClick(e: ActionEvent): void {
    const { currentTarget } = e
    const isAllDrawersClosed = this.drawerOutlets.every((outlet) => !outlet.openValue)
    const allSelected = this.isFacilitySelected && this.isRoomsSelected && this.isDateSelected

    // When user open it from change booking slideout
    if (isAllDrawersClosed) {
      const drawer = this.drawerOutlets.find((drawer) =>
        drawer.element.contains(currentTarget as HTMLElement)
      )!

      drawer.open()
    } else if (allSelected) {
      if (this.isPaxDrawerOpen) {
        this.lastStepEditButtonTarget.setAttribute("form", this.bookingFooterOutlet.paxFormIdValue)
      } else {
        this.lastStepEditButtonTarget.setAttribute(
          "form",
          this.bookingFooterOutlet.calendarFormIdValue
        )
      }
      this.lastStepEditButtonTarget.click()
      // here we need to submit calendar form to refresh rooms/facilities
    } else {
      this.submitForm()
    }
  }

  onPaxDrawerOpen(): void {
    setTimeout(() => {
      const allSelected = this.isDateSelected && this.isFacilitySelected
      this.bookingFooterOutlet.setPaxSubmit(allSelected)
    }, 0)
  }

  onFacilityDrawerOpen(e: ActionEvent): void {
    this.bookingFooterOutlet.setFacilitiesSubmit(!!this.selectedFacilityRadio)
    this.scrollDrawerIntoView(e)
  }

  onRoomDrawerOpen(e: ActionEvent): void {
    const remainingPax = this.selectedPax - this.paxForSelectedRooms
    this.bookingFooterOutlet.setRoomsSubmit(remainingPax === 0)

    this.scrollDrawerIntoView(e)
  }

  roomSelected({ detail }: DispatchEvent<{ delta: number; name: string }>): void {
    const remainingPax = this.selectedPax - this.paxForSelectedRooms
    const { delta, name } = detail

    this.roomCounterOutlets.forEach((counter) => {
      // Counters with the same `name` represent one room, with different occupancies. The available
      // units for a room are for all occupancies, so when one changes we increase/decrease
      // availability of all of them. This value is used in max calculation, so must be set before
      // max below.
      if (counter.name === name) {
        counter.setAvailable(delta)
      }

      counter.setMax(remainingPax)
    })

    this.totalPrice = this.roomCounterOutlets.reduce(
      (sum, counter) => sum + counter.selectedPrice,
      0
    )
    this.bookingFooterOutlet.setPriceTarget(this.totalPrice)
    this.bookingFooterOutlet.setRoomsSubmit(remainingPax === 0)
    this.updateSelectedRoomsInputs()
  }

  saveDate(): void {
    if (this.selectedDateRadio) {
      const isoDate = this.selectedDateRadio.value
      const formattedDate = formatEEEDMMMYYYY(new Date(isoDate))
      const formattedNights =
        this.nightsValue > 0 ? `(${pluralize(this.nightsValue, "night")})` : ""

      this.updateSummary(this.calendarDrawerSummaryTarget, `${formattedDate} ${formattedNights}`)
    } else {
      this.updateSummary(this.calendarDrawerSummaryTarget, "No date selected", false)
    }
  }

  saveFacility(): void {
    if (this.selectedFacilityRadio) {
      const selectedFacility = this.selectedFacilityRadio.parentElement!.querySelector(
        ".js-facility-description"
      )!.textContent

      this.updateSummary(this.facilitiesDrawerSummaryTarget, selectedFacility)
    } else {
      this.updateSummary(this.facilitiesDrawerSummaryTarget, "No facility selected", false)
      this.hiddenFacilityInputTargets.forEach((input) => (input.value = ""))
    }
  }

  onPaxChange({ detail }: DispatchEvent<{ count: number; max: number }>): void {
    const { count, max } = detail

    this.resetRoomsSelection()
    this.toggleMaxPaxNotice(count < max)
    this.hiddenPaxInputTargets.forEach((input) => {
      input.value = count.toString()
    })

    if (!this.isSpaDay) {
      this.bookingFooterOutlet.setPaxSubmit(false)
    }
  }

  saveRooms(): void {
    if (this.paxForSelectedRooms > 0) {
      const selectedRooms = this.selectedRooms
      const roomCounts = [...selectedRooms].reduce((acc, [counter, counts]) => {
        acc.set(counter.roomNameValue, (acc.get(counter.roomNameValue) ?? 0) + counts.length)
        return acc
      }, new Map<string, number>())

      const summaryText = [...roomCounts]
        .map(([roomName, count]) => `${count} x ${roomName}`)
        .join(", ")
      this.updateSummary(this.roomsDrawerSummaryTarget, summaryText)
    } else {
      this.resetRoomsSelection()
    }
  }

  scrollDrawerIntoView({ currentTarget }: ActionEvent): void {
    const drawer = this.drawerOutlets.find((drawer) =>
      drawer.element.contains(currentTarget as HTMLElement)
    )!

    // Date drawer is a special case -- we want to scroll to the selected date, not top of drawer.
    const { selectedDateRadio } = this
    const elementToScroll =
      selectedDateRadio && drawer.element.contains(selectedDateRadio)
        ? selectedDateRadio
        : drawer.element

    drawer.afterTransition(() => {
      // Set scroll margin offset to modal header height, plus padding.
      const PADDING = 16
      drawer.element.style.setProperty(
        "--drawer-top-offset",
        `${this.modalHeaderTarget.offsetHeight + PADDING}px`
      )

      elementToScroll.scrollIntoView({ behavior: "smooth" })
    })
  }

  submitForm(): void {
    this.formSubmitButtonTarget.click()
  }

  private toggleMaxPaxNotice(hide: boolean): void {
    this.maxPaxNoticeTarget.hidden = hide
  }

  resetRoomsSelection(): void {
    if (this.hasRoomsStreamTarget) {
      this.hiddenRoomsInputsContainerTargets.forEach((container) => (container.innerHTML = ""))
      this.updateSummary(this.roomsDrawerSummaryTarget, "No rooms selected", false)
    }
  }

  private updateSummary(target: HTMLElement, summaryText: string, show: boolean = true): void {
    const summaryTextElement = target.querySelector(".js-summary-text") as HTMLElement
    const checkIcon = target.querySelector(".js-summary-check-icon") as HTMLElement
    const editButton = target.querySelector(".js-summary-edit-button") as HTMLButtonElement

    summaryTextElement.textContent = summaryText
    checkIcon.hidden = !show
    editButton.hidden = !show
  }

  private get selectedDateRadio(): HTMLInputElement | undefined {
    return this.dateRadioTargets.find((radio) => radio.checked)
  }

  private get selectedFacilityRadio(): HTMLInputElement | undefined {
    return this.facilityRadioTargets.find((radio) => radio.checked)
  }

  private get selectedRooms(): Map<RoomCounterController, number[]> {
    return this.roomCounterOutlets.reduce((rooms, roomCounter) => {
      const { countValue, occupancyValue } = roomCounter

      if (countValue > 0) {
        const occupancies = rooms.get(roomCounter) ?? []

        for (let i = 0; i < countValue; i++) {
          occupancies.push(occupancyValue)
        }

        rooms.set(roomCounter, occupancies)
      }

      return rooms
    }, new Map<RoomCounterController, number[]>())
  }

  private get selectedPax(): number {
    return +this.paxInputTarget.value
  }

  private setCalendarSubmit(isDateSelected: boolean): void {
    const allSelected = this.isFacilitySelected && this.isRoomsSelected
    this.bookingFooterOutlet.setCalendarSubmit({
      isDateSelected,
      allSelected
    })
  }

  private get paxForSelectedRooms(): number {
    return this.roomCounterOutlets.reduce((sum, counter) => sum + counter.selectedPax, 0)
  }

  private updateSelectedRoomsInputs(): void {
    this.hiddenRoomsInputsContainerTargets.forEach((container) => (container.innerHTML = ""))

    const selectedRooms = this.selectedRooms
    selectedRooms.forEach((occupancies, roomCounter) => {
      occupancies.forEach((occupancy) => {
        this.hiddenRoomsInputsContainerTargets.forEach((container) => {
          const input = document.createElement("input")
          input.type = "hidden"
          input.name = `selected_rooms[${roomCounter.roomIdValue}][]`
          input.value = occupancy.toString()
          container.appendChild(input)
        })
      })
    })
  }

  private get isSpaDay(): boolean {
    return this.nightsValue === 0
  }

  private get isFacilitySelected(): boolean {
    return this.hiddenFacilityInputTargets.length
      ? !!this.hiddenFacilityInputTargets[0].value
      : true
  }

  private get isRoomsSelected(): boolean {
    return this.isSpaDay ? true : this.paxForSelectedRooms === this.selectedPax
  }

  private get isDateSelected(): boolean {
    return !!this.hiddenDateInputTargets[0].value
  }

  private get isPaxDrawerOpen(): boolean {
    const paxDrawer = this.drawerOutlets.find((drawer) =>
      drawer.element.contains(this.paxSummaryTarget)
    )!

    return paxDrawer.openValue
  }
}
